import styled from 'styled-components';
import { Tooltip } from 'design-system/src/components/Tooltip/Tooltip';
import { FieldState } from '../../../lib/formFiller';
import { roundArrow } from 'tippy.js';
import 'tippy.js/dist/svg-arrow.css'; // CSS for SVG arrows
import { bodyFontSizeStyles } from '../styles';
/**
 * Forms contain both fields assigned to the current signer and fields assigned to the
 * other signer
 *
 * We want to tab navigate through the signer's assigned fields first, but still have the
 * other fields be accessible via tab navigation + have a tooltip, so we wrap the non-interactive
 * fields in a FocusableWrapper.
 *
 * Note we do not use `childNotInteractive` on `Tooltip` because we have custom styling
 */

const getTooltipContent = (fieldState: FieldState, autoFilledDate?: boolean): string => {
  if (!fieldState.isAssigned) {
    return `${fieldState.role} only`;
  } else if (autoFilledDate) {
    return 'This date cannot be changed and represents when the document has been signed and completed.';
  } else if (!fieldState.isRequired) {
    return 'Optional';
  } else {
    return 'Required';
  }
};

const FocusableWrapper = styled.span`
  height: 100%;
  width: 100%;
  // stops icon children from being off-center
  display: flex;
  align-items: center;
`;

const TooltipContent = styled.div`
  // TODO(tokens): Replace hex color value #bcb3c2 with numbered equivalent once available.
  color: #bcb3c2;
  ${bodyFontSizeStyles}
`;

export function WithOptionalTooltip({
  fieldState,
  autoFilledDate,
  children,
}: {
  fieldState: FieldState;
  autoFilledDate?: boolean;
  children: React.ReactElement;
}) {
  const tooltipContent = getTooltipContent(fieldState, autoFilledDate);

  // For disabled/read-only fields, we need to tell Tooltip that the child is not interactive
  // so it can wrap it in a focusable element that can show tooltips
  const isChildNotInteractive = fieldState.isReadOnly || autoFilledDate;

  return (
    <Tooltip content={(<TooltipContent>{tooltipContent}</TooltipContent>) as any} arrow={roundArrow}>
      {isChildNotInteractive ? <FocusableWrapper>{children}</FocusableWrapper> : children}
    </Tooltip>
  );
}
